import { useRef, useState, type ReactNode } from 'react';

import { cn } from '@rocco/utils/cn';
import type { ShareOptions } from '~/modules/social-sharing/utils/sharing';
import {
    SHARE_SHEET_HEIGHT,
    SHARE_SHEET_SURFACE,
    SHARE_SHEET_WIDTH,
    ShareTargets,
    useDismissShareSheet,
    useShareSelect,
} from './TileShareMenu';
import { CONTROL_RADIUS } from './tile-action-control';

/**
 * The share sheet hung under a control of the host's own.
 *
 * The tile's sheet is the disc growing into the box, so it lives inside the
 * tile. A surface with its own Share control — the photo viewer's pill — wants
 * the same box, the same targets and the same dismissal, arriving under that
 * control instead. The trigger is rendered by the host through `children`,
 * with the `open` state and its toggle handed to it, so the sheet knows what
 * to sit under and what not to count as a click outside.
 */
export const ShareSheet = ({
    share,
    children,
    placement = 'below',
}: {
    /** What is being shared, including the picture the pin needs. */
    share: ShareOptions;
    /** The control that opens the sheet. */
    children: (state: { open: boolean; toggle: () => void }) => ReactNode;
    /**
     * Which side of the control the sheet hangs from. `above` for a control
     * pinned to the foot of the window, where below is off the screen.
     */
    placement?: 'below' | 'above';
}) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLSpanElement>(null);
    useDismissShareSheet(open, wrapRef, setOpen);
    const handleSelect = useShareSelect(share, setOpen);

    return (
        <span ref={wrapRef} className="relative inline-flex">
            {children({ open, toggle: () => setOpen(!open) })}
            <div
                className={cn(
                    'absolute right-0 z-20 overflow-hidden',
                    placement === 'above'
                        ? 'bottom-[calc(100%+0.5rem)] origin-bottom-right'
                        : 'top-[calc(100%+0.5rem)] origin-top-right',
                    SHARE_SHEET_SURFACE,
                    'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                    open
                        ? 'scale-100 opacity-100'
                        : 'pointer-events-none scale-95 opacity-0',
                )}
                style={{
                    width: SHARE_SHEET_WIDTH,
                    height: SHARE_SHEET_HEIGHT,
                    borderRadius: CONTROL_RADIUS,
                }}
                aria-hidden={!open}
            >
                <ShareTargets
                    share={share}
                    open={open}
                    onSelect={handleSelect}
                    className="h-full"
                />
            </div>
        </span>
    );
};
